import Cookies from 'js-cookie';

/**
 * Dynamically injects font resources for a given brand into the document <head>.
 * Font data comes from window.merlinBrandFonts (injected by FontConfigScript.cshtml),
 * which is serialised from BrandFontConfig.cs — the single source of truth.
 */
function loadFontsForBrand(brand, urlPrefix) {
    const fonts = window.merlinBrandFonts?.[brand];
    if (!fonts) return;

    const head = document.head;

    (fonts.preconnect || []).forEach(href => {
        const link = document.createElement('link');
        link.rel = 'preconnect';
        link.href = href;
        link.crossOrigin = '';
        head.appendChild(link);
    });

    (fonts.stylesheets || []).forEach(({ href }) => {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = href;
        link.crossOrigin = '';
        head.appendChild(link);
    });

    (fonts.local || []).forEach(({ path, type }) => {
        const link = document.createElement('link');
        link.rel = 'preload';
        link.as = 'font';
        link.href = `${urlPrefix}${path}`;
        link.type = type;
        link.crossOrigin = '';
        head.appendChild(link);
    });

    (fonts.scripts || []).forEach(({ src, inline }) => {
        const script = document.createElement('script');
        script.src = src;
        if (inline) {
            script.onload = function() {
                // eslint-disable-next-line no-eval
                try { eval(inline); } catch (e) { /* ignore */ }
            };
        }
        head.appendChild(script);
    });
}

/**
 * Client-side theme switching for CDN caching.
 * Server renders with configured brand/site; this script switches theme based on cookies.
 */
window.addEventListener("DOMContentLoaded", function() {
    const themeBrand = Cookies.get('merlin-theme-brand');
    const themeSite = Cookies.get('merlin-theme-site');

    // No theme cookies, use server-rendered configured brand
    if (!themeBrand && !themeSite) {
        return;
    }

    const htmlElement = document.documentElement;
    const configuredBrand = htmlElement.getAttribute('data-brandname');
    const configuredSite = htmlElement.getAttribute('data-sitename');

    // Determine effective theme
    const effectiveBrand = themeBrand || configuredBrand || 'neutral';
    const effectiveSite = themeSite || configuredSite || '';

    // Only proceed if theme differs from configured
    if (effectiveBrand === configuredBrand && effectiveSite === configuredSite) {
        return;
    }

    console.log(`Theme switching: ${configuredBrand}/${configuredSite} → ${effectiveBrand}/${effectiveSite}`);

    // Update HTML data attributes
    htmlElement.setAttribute('data-brandname', effectiveBrand);
    if (effectiveSite) {
        htmlElement.setAttribute('data-sitename', effectiveSite);
    }

    // Update window.merlinConfig if it exists
    if (window.merlinConfig) {
        window.merlinConfig.brandName = effectiveBrand;
        if (effectiveSite) {
            window.merlinConfig.siteName = effectiveSite;
        }
    }

    const urlPrefix = window.merlinConfig?.urlPrefix || '';

    // Load alternative theme CSS by directly accessing the theme directory
    if (effectiveBrand !== configuredBrand) {
        // Find the main stylesheet link (has cache busting in href like style.123456.css)
        const mainStylesheet = document.querySelector('link[rel="stylesheet"][href*="style"]');

        if (mainStylesheet) {
            // Build direct path to theme CSS file (bypassing RewriteCssMiddleware)
            const themeCssPath = `${urlPrefix}/dist/styles/themes/${effectiveBrand}/css/styles.css`;

            console.log('Loading theme CSS:', themeCssPath);

            // Replace the main stylesheet href to point to the new theme
            mainStylesheet.href = themeCssPath;

            // Add a data attribute to track theme override
            mainStylesheet.setAttribute('data-theme-override', effectiveBrand);
        } else {
            console.warn('Main stylesheet not found, cannot apply theme override');
        }

        // Inject font resources for the target brand.
        // Data comes from window.merlinBrandFonts (see FontConfigScript.cshtml / BrandFontConfig.cs).
        // Keys in merlinBrandFonts are lowercase (BrandNameEnum.ToString().ToLower()),
        // but the cookie may store the original enum casing (e.g. "HeidePark"), so normalise here.
        loadFontsForBrand(effectiveBrand.toLowerCase(), urlPrefix);
    }
});
